import { getWebSocketAuthParams } from "src/config/websocketConfig";
import { LOW_RES_INTERVAL, HIGH_RES_INTERVAL } from "src/store/inspectionCaptureSlice";

interface StreamingOptions {
  quality: number; // JPEG quality (0-1)
  maxWidth: number; // Max width to resize image to
  maxHeight: number; // Max height to resize image to
  interval: number; // Milliseconds between frames
  screenWidth: number; // Screen width for calculating resolution
  screenHeight: number; // Screen height for calculating resolution
  resolutionScale: number; // Scale factor for low-res stream (0.3, 0.5, 0.8, 1.0)
  containerId?: string; // Optional container ID
  targetClasses?: string[]; // Optional target classes for detection
}

export type ResolutionMode = 'low' | 'high';

interface MetricsCallback {
  (metrics: { imageSize?: number; latency?: number; resolution?: ResolutionMode }): void;
}

interface DetectionResult {
  status: string;
  bboxes?: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
    class?: string;
    class_id?: number;
    label?: string;
    confidence: number;
  }>;
  guidance_message?: string;
  image_next_stage?: string; // "LOW_RES" | "HIGH_RES" | "READY_FOR_ANALYZER"
  analysis_status?: string; // "queued" | "analyzing" | "complete"
  detection_error?: string;
  image_info?: {
    size_bytes: number;
    dimensions: [number, number];
    detection_count: number;
  };
}

/**
 * Service for streaming camera images over WebSocket
 * Uses low-quality continuous frames for real-time object detection
 */
class ImageStreamingWebSocketService {
  private ws: WebSocket | null = null;
  private streamingActive = false;
  private streamingPaused = false; // New flag to track paused state
  private videoElement: HTMLVideoElement | null = null;
  private highResVideoElement: HTMLVideoElement | null = null;
  private currentVideoElement: HTMLVideoElement | null = null; // Track which stream is active
  private streamingInterval: number | null = null;
  private reconnectInterval: number | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 2000; // Start with 2 seconds, will increase exponentially
  private lastActivity = Date.now();
  private wsUrl: string;
  private clientId: string;
  private inspectionId?: string;
  private activeQuestionIds?: string[];
  private onDetectionResultCallback: ((result: DetectionResult) => void) | null = null;
  private onConnectionStatusCallback: ((connected: boolean) => void) | null = null;
  private onMetricsCallback: MetricsCallback | null = null;
  // Track send time + resolution per frame for latency calculation. Resolution
  // is stored so each latency sample can be attributed to the low/high-res
  // frame it actually corresponds to (responses are paired FIFO).
  private frameSentTimestamps: Map<number, { sendTime: number; resolution: ResolutionMode }> = new Map();
  // Current capture resolution. Streaming starts in low-res and flips to high
  // when the backend requests it via switchToHighRes().
  private currentResolution: ResolutionMode = "low";
  private options: StreamingOptions;

  // Persistent canvas for frame capture - reused to prevent flash
  private captureCanvas: HTMLCanvasElement | null = null;
  private captureContext: CanvasRenderingContext2D | null = null;

  constructor(
    wsUrl: string,
    clientId: string,
    inspectionId: string,
    activeQuestionIds: string[],
    options: StreamingOptions
  ) {
    this.wsUrl = wsUrl;
    this.clientId = clientId;
    this.inspectionId = inspectionId;
    this.activeQuestionIds = activeQuestionIds;
    this.options = options;
  }

  /**
   * Set metrics callback for dev mode
   */
  setMetricsCallback(callback: MetricsCallback): void {
    this.onMetricsCallback = callback;
  }

  /**
   * Start streaming video frames from the provided video elements
   */
  async startStreaming(
    lowResVideoElement: HTMLVideoElement,
    highResVideoElement: HTMLVideoElement,
    onDetectionResult: (result: DetectionResult) => void,
    onConnectionStatus?: (connected: boolean) => void,
    onMetrics?: MetricsCallback
  ): Promise<void> {
    if (this.streamingActive) {
      return;
    }

    this.videoElement = lowResVideoElement;
    this.highResVideoElement = highResVideoElement;
    this.currentVideoElement = lowResVideoElement; // Start with low-res
    this.onDetectionResultCallback = onDetectionResult;
    this.onConnectionStatusCallback = onConnectionStatus || null;
    this.onMetricsCallback = onMetrics || null;
    
    // Connect WebSocket first
    await this.connect();
    
    this.streamingActive = true;
    this.captureAndSendFrames();
  }

  /**
   * Builds query parameters for WebSocket connection
   * @param authParams - Authentication parameters from getWebSocketAuthParams
   * @returns URLSearchParams with all required query parameters
   */
  private buildQueryParams(authParams: URLSearchParams): URLSearchParams {
    const queryParams = new URLSearchParams(authParams);

    // Add inspection_id if present
    if (this.inspectionId) {
      queryParams.set('inspection_id', this.inspectionId);
    }

    // Add active_question_ids if present (comma-separated list)
    if (this.activeQuestionIds && this.activeQuestionIds.length > 0) {
      queryParams.set('active_question_ids', this.activeQuestionIds.join(','));
    }

    // Add optional object_id if container_id is present
    if (this.options.containerId) {
      queryParams.set('object_id', this.options.containerId);
    }

    // Add optional target_classes if present (comma-separated list)
    if (this.options.targetClasses && this.options.targetClasses.length > 0) {
      queryParams.set('target_classes', this.options.targetClasses.join(','));
    }

    return queryParams;
  }

  /**
   * Connect to the WebSocket server with authentication
   * Includes automatic reconnection logic and comprehensive error handling
   */
  async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    return new Promise(async (resolve, reject) => {
      try {
        // Get authentication parameters for WebSocket connection
        // This handles both Cognito and Federate authentication automatically
        const authParams = await getWebSocketAuthParams();

        if (authParams.toString() === '') {
          console.warn("No authentication parameters available for WebSocket connection");
        }

        // Build query parameters with all required fields
        const queryParams = this.buildQueryParams(authParams);

        // Connect to WebSocket endpoint with client ID, authentication, and inspection_id
        const wsUrl = `${this.wsUrl}/ws/media/stream/${this.clientId}?${queryParams.toString()}`;
        console.info(`Connecting to WebSocket at: ${wsUrl}`);
        this.ws = new WebSocket(wsUrl);
      } catch (error) {
        const errorMessage = "Failed to get authentication parameters for WebSocket";
        console.error(errorMessage, error);
        if (this.onConnectionStatusCallback) {
          this.onConnectionStatusCallback(false);
        }
        reject(new Error(`${errorMessage}: ${error}`));
        return;
      }

      // Set up connection timeout
      const connectionTimeout = setTimeout(() => {
        if (this.ws?.readyState !== WebSocket.OPEN) {
          console.error("WebSocket connection timeout");
          if (this.onConnectionStatusCallback) {
            this.onConnectionStatusCallback(false);
          }
          this.ws?.close();
          reject(new Error("WebSocket connection timeout"));
        }
      }, 10000); // 10 second timeout

      this.ws.onopen = () => {
        clearTimeout(connectionTimeout);
        if (this.onConnectionStatusCallback) {
          this.onConnectionStatusCallback(true);
        }
        this.lastActivity = Date.now();
        resolve();
      };

      this.ws.onmessage = (event) => {
        try {
          const result: DetectionResult = JSON.parse(event.data);
          const receiveTime = Date.now();
          this.lastActivity = receiveTime;
          
          // Calculate latency if we have the original send timestamp
          // The backend doesn't echo back timestamps, so we'll estimate based on receive time
          // and use an approximate latency calculation
          if (this.frameSentTimestamps.size > 0) {
            // Get the oldest timestamp (approximate the response for that frame)
            const oldestTimestamp = Array.from(this.frameSentTimestamps.keys())[0];
            const sentEntry = this.frameSentTimestamps.get(oldestTimestamp);
            
            if (sentEntry) {
              const latency = receiveTime - sentEntry.sendTime;
              this.frameSentTimestamps.delete(oldestTimestamp);
              
              // Report latency metrics, tagged with the resolution of the
              // frame this response corresponds to.
              if (this.onMetricsCallback) {
                this.onMetricsCallback({ latency, resolution: sentEntry.resolution });
              }
              
              console.debug(`Round-trip latency: ${latency}ms`);
            }
          }
          
          // Check for server-side errors in the response
          if (result.status === "error" && result.detection_error) {
            console.warn("Server detection error:", result.detection_error);
          }
          
          // Pass detection results to callback
          if (this.onDetectionResultCallback) {
            this.onDetectionResultCallback(result);
          }
        } catch (error) {
          console.error("Error parsing WebSocket message:", error, event.data);
        }
      };

      this.ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        
        // Log appropriate message based on close code
        if (event.code === 1000) {
          // Normal closure
        } else if (event.code === 1006) {
          console.error("WebSocket connection closed abnormally (server may be down)");
        } else {
          console.warn(`WebSocket connection closed: Code ${event.code}, Reason: ${event.reason || "Unknown"}`);
        }
        
        if (this.onConnectionStatusCallback) {
          this.onConnectionStatusCallback(false);
        }
        
        // Try to reconnect if not a normal closure and streaming is still active
        if (event.code !== 1000 && this.streamingActive) {
          this.attemptReconnect();
        } else {
          this.stopStreaming();
          reject(new Error(`WebSocket closed: ${event.code} ${event.reason || "No reason provided"}`));
        }
      };

      this.ws.onerror = (error) => {
        clearTimeout(connectionTimeout);
        console.error("WebSocket error:", error);
        if (this.onConnectionStatusCallback) {
          this.onConnectionStatusCallback(false);
        }
        reject(error);
      };
    });
  }

  /**
   * Attempts to reconnect to the WebSocket server with exponential backoff
   */
  private attemptReconnect(): void {
    // Clear any existing reconnect interval
    if (this.reconnectInterval !== null) {
      clearInterval(this.reconnectInterval);
      this.reconnectInterval = null;
    }
    
    // Calculate exponential backoff delay with jitter
    const delay = Math.min(
      this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts) * (0.9 + Math.random() * 0.2),
      30000 // Max 30 second delay
    );
    
    setTimeout(async () => {
      // Check if we should stop trying
      if (!this.streamingActive || this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.warn("Maximum reconnection attempts reached or streaming stopped");
        this.stopStreaming();
        return;
      }
      
      this.reconnectAttempts++;
      
      try {
        await this.connect();
        
        // If we get here, connection was successful
        this.reconnectAttempts = 0; // Reset counter on success

        // Clear stale send timestamps from the dropped connection. Otherwise
        // the FIFO latency pairing would match new responses against pre-
        // disconnect sends, producing wildly inflated latency and wrong
        // resolution attribution.
        this.frameSentTimestamps.clear();

        // Resume sending frames if we have a video element
        if (this.videoElement && this.streamingActive) {
          this.captureAndSendFrames();
        }
      } catch (error) {
        console.error("Reconnection failed:", error);
        
        // Try again if we haven't hit the limit
        if (this.reconnectAttempts < this.maxReconnectAttempts && this.streamingActive) {
          this.attemptReconnect();
        } else {
          console.error("Giving up on reconnection");
          this.stopStreaming();
        }
      }
    }, delay);
  }

  /**
   * Internal method to switch between video streams with different configurations
   */
  private switchStream(
    videoElement: HTMLVideoElement | null,
    config: { quality: number; maxWidth: number; maxHeight: number; interval: number }
  ): void {
    if (videoElement) {
      this.currentVideoElement = videoElement;
      this.options = { ...this.options, ...config };

      if (this.streamingActive && !this.streamingPaused) {
        this.captureAndSendFrames();
      }
    }
  }

  /**
   * Switch to high-res video stream
   * Uses 100% of screen resolution for maximum quality
   */
  switchToHighRes(): void {
    // High-res always uses 100% of screen size
    const highResWidth = this.options.screenWidth || 1920;
    const highResHeight = this.options.screenHeight || 1080;

    this.currentResolution = "high";
    this.switchStream(
      this.highResVideoElement,
      { quality: 0.95, maxWidth: highResWidth, maxHeight: highResHeight, interval: HIGH_RES_INTERVAL }
    );
  }

  /**
   * Switch back to low-res video stream
   * Uses configured resolution scale (e.g., 50% of screen size)
   */
  switchToLowRes(): void {
    // Low-res uses the configured resolution scale
    const resolutionScale = this.options.resolutionScale || 0.5;
    const lowResWidth = Math.round((this.options.screenWidth || 1280) * resolutionScale);
    const lowResHeight = Math.round((this.options.screenHeight || 720) * resolutionScale);
    
    this.currentResolution = "low";
    this.switchStream(
      this.videoElement,
      { quality: 0.6, maxWidth: lowResWidth, maxHeight: lowResHeight, interval: LOW_RES_INTERVAL }
    );
  }

  /**
   * Pauses frame transmission without closing the WebSocket connection
   * This allows the connection to stay alive during photo capture and analysis
   */
  pauseStreaming(): void {
    if (!this.streamingPaused) {
      this.streamingPaused = true;
      
      // Stop the interval that sends frames
      if (this.streamingInterval !== null) {
        window.clearInterval(this.streamingInterval);
        this.streamingInterval = null;
      }
      
      // Keep the WebSocket connection open
    }
  }
  
  /**
   * Resumes frame transmission on the existing WebSocket connection
   */
  resumeStreaming(): void {
    if (this.streamingPaused && this.streamingActive) {
      this.streamingPaused = false;
      
      // Restart sending frames if we have a video element and an active connection
      if (this.currentVideoElement && this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.captureAndSendFrames();
      }
    }
  }

  /**
   * Stop streaming and close WebSocket connection
   */
  stopStreaming(): void {
    this.streamingActive = false;
    this.streamingPaused = false;
    
    if (this.streamingInterval !== null) {
      window.clearInterval(this.streamingInterval);
      this.streamingInterval = null;
    }
    
    if (this.reconnectInterval !== null) {
      clearInterval(this.reconnectInterval);
      this.reconnectInterval = null;
    }
    
    this.reconnectAttempts = 0;
    this.disconnect();
    this.videoElement = null;
    this.highResVideoElement = null;
    this.currentVideoElement = null;
    this.onDetectionResultCallback = null;
  }

  /**
   * Closes WebSocket connection
   */
  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Captures video frames at regular intervals and sends them over WebSocket
   */
  private captureAndSendFrames(): void {
    if (this.streamingInterval !== null) {
      window.clearInterval(this.streamingInterval);
    }

    // Start capturing frames at the configured interval
    this.streamingInterval = window.setInterval(() => {
      // Don't send frames if streaming is paused, inactive, or connection is not open
      if (this.streamingPaused || !this.streamingActive || !this.currentVideoElement || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
        return;
      }

      // Capture frame from current video element (low-res or high-res)
      const frame = this.captureVideoFrame();
      if (!frame) return;

      // Send frame over WebSocket - ensure format matches backend expectations
      try {
        // Format message to exactly match what backend expects
        const message = {
          image: frame,
          metadata: {
            timestamp: Date.now(),
            client_id: this.clientId,
            streaming_mode: true,
            width: this.options.maxWidth,
            height: this.options.maxHeight,
            quality: this.options.quality
          }
        };
        
        // Send the message and track metrics
        const messageStr = JSON.stringify(message);
        const imageSizeKB = Math.round(messageStr.length / 1024);
        const timestamp = message.metadata.timestamp;
        
        // Track send time + resolution for latency calculation
        this.frameSentTimestamps.set(timestamp, { sendTime: Date.now(), resolution: this.currentResolution });
        
        // Clean up old timestamps (keep only last 50)
        if (this.frameSentTimestamps.size > 50) {
          const oldestKey = Array.from(this.frameSentTimestamps.keys())[0];
          this.frameSentTimestamps.delete(oldestKey);
        }
        
        // Report image size metrics, tagged with the current resolution.
        if (this.onMetricsCallback) {
          this.onMetricsCallback({ imageSize: imageSizeKB, resolution: this.currentResolution });
        }
        
        console.debug(`Sending frame: ${imageSizeKB}KB`);
        this.ws.send(messageStr);
      } catch (error) {
        console.error("Error sending frame over WebSocket:", error);
      }
    }, this.options.interval);
  }

  /**
   * Captures a single frame from the current video element and returns as base64
   * Uses persistent canvas to prevent flash caused by repeated canvas creation
   */
  private captureVideoFrame(): string | null {
    if (!this.currentVideoElement) return null;
    
    const video = this.currentVideoElement;
    if (video.videoWidth === 0 || video.videoHeight === 0) return null;
    
    // Create canvas once and reuse (prevents flash)
    if (!this.captureCanvas) {
      this.captureCanvas = document.createElement("canvas");
      this.captureContext = this.captureCanvas.getContext("2d");
    }
    
    if (!this.captureContext) return null;
    
    // Only resize canvas if video dimensions changed
    if (this.captureCanvas.width !== video.videoWidth || this.captureCanvas.height !== video.videoHeight) {
      this.captureCanvas.width = video.videoWidth;
      this.captureCanvas.height = video.videoHeight;
    }
    
    // Draw at full resolution
    this.captureContext.drawImage(video, 0, 0);
    
    // Convert to base64 JPEG with configured quality
    try {
      const frame = this.captureCanvas.toDataURL("image/jpeg", this.options.quality);
      return frame;
    } catch (error) {
      console.error("Error converting canvas to base64:", error);
      return null;
    }
  }

  /**
   * Updates streaming options
   */
  updateOptions(options: Partial<StreamingOptions>): void {
    this.options = { ...this.options, ...options };

    // If streaming is active, restart it with new options
    if (this.streamingActive && this.videoElement) {
      this.captureAndSendFrames();
    }
  }

  /**
   * Checks if WebSocket is connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Checks if streaming is currently active
   */
  isStreamingActive(): boolean {
    return this.streamingActive;
  }
  
  /**
   * Checks if streaming is currently paused
   */
  isStreamingPaused(): boolean {
    return this.streamingPaused;
  }
  
  /**
   * Gets the current status of the connection
   */
  getConnectionStatus(): string {
    if (!this.ws) return 'disconnected';
    
    switch(this.ws.readyState) {
      case WebSocket.CONNECTING: return 'connecting';
      case WebSocket.OPEN: return 'connected';
      case WebSocket.CLOSING: return 'closing';
      case WebSocket.CLOSED: return 'closed';
      default: return 'unknown';
    }
  }
}

export default ImageStreamingWebSocketService;
